import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Mail, Megaphone, MessageSquare, Send } from 'lucide-react';
import { ModuleHero } from '../components/ModuleHero';
import { EventWorkspaceSection } from '../components/PageTemplates';
import type { CommunicationDelivery, CommunicationMessage } from '../models';
import { ROLE_LABEL } from '../models/user';
import {
  canMessageTournament,
  listCommunications,
  listCommunicationThreads,
  listThreadDeliveries,
  listThreadMessages,
} from '../services/communicationHubService';
import type { AppDatabase } from '../services/store';
import { useStore } from '../hooks/useStore';
import { useActiveTournament } from '../state/TournamentContext';

type DeliveryCounts = Record<CommunicationDelivery['status'], number>;

function formatDateTime(value?: string): string {
  if (!value) return 'Not sent';
  return new Date(value).toLocaleString();
}

function formatStatus(value: string): string {
  return value.replace(/_/g, ' ');
}

function plural(value: number, singular: string, pluralValue = `${singular}s`): string {
  return value === 1 ? singular : pluralValue;
}

function deliveryCounts(deliveries: CommunicationDelivery[]): DeliveryCounts {
  return deliveries.reduce<DeliveryCounts>(
    (counts, delivery) => {
      counts[delivery.status] += 1;
      return counts;
    },
    { queued: 0, sent: 0, delivered: 0, failed: 0, skipped: 0 },
  );
}

function audienceSummary(message: CommunicationMessage, db: AppDatabase): string {
  const audience = message.audience ?? {};
  const parts: string[] = [];

  if (audience.roles?.length) {
    parts.push(`Roles: ${audience.roles.map((role) => ROLE_LABEL[role]).join(', ')}`);
  }
  if (audience.divisionIds?.length) {
    const names = audience.divisionIds
      .map((id) => db.divisions.find((division) => division.id === id)?.name)
      .filter(Boolean);
    parts.push(`Divisions: ${names.length > 0 ? names.join(', ') : audience.divisionIds.length}`);
  }
  if (audience.teamIds?.length) {
    const names = audience.teamIds
      .map((id) => db.teams.find((team) => team.id === id)?.name)
      .filter(Boolean);
    parts.push(`Teams: ${names.length > 0 ? names.join(', ') : audience.teamIds.length}`);
  }
  if (audience.profileIds?.length) {
    const names = audience.profileIds
      .map((id) => db.profiles.find((profile) => profile.id === id)?.displayName)
      .filter(Boolean);
    parts.push(`People: ${names.length > 0 ? names.join(', ') : audience.profileIds.length}`);
  }
  if (audience.tags?.length) {
    parts.push(`Tags: ${audience.tags.join(', ')}`);
  }

  return parts.length > 0 ? parts.join(' · ') : 'Full event audience';
}

function MessageChannels({ message }: { message: CommunicationMessage }) {
  return (
    <div className="row" style={{ gap: 6 }}>
      {message.channels.includes('email') && <span className="chip"><Mail size={11} /> email</span>}
      {message.channels.includes('in_app') && <span className="chip"><MessageSquare size={11} /> in-app</span>}
    </div>
  );
}

export function EventCommunicationHistoryPage() {
  const db = useStore((state) => state);
  const { tournament } = useActiveTournament();
  const messages = useMemo(() => listCommunications(tournament.id), [db.communications, tournament.id]);
  const threads = useMemo(() => listCommunicationThreads({ tournamentId: tournament.id }), [db.communicationThreads, tournament.id]);
  const [selectedMessageId, setSelectedMessageId] = useState('');
  const selectedMessage = messages.find((message) => message.id === selectedMessageId) ?? messages[0];
  const composeLink = `/admin/comms?eventId=${encodeURIComponent(tournament.id)}`;
  const canComposeForEvent = canMessageTournament(tournament);

  useEffect(() => {
    if (!selectedMessageId && messages[0]) setSelectedMessageId(messages[0].id);
    if (selectedMessageId && !messages.some((message) => message.id === selectedMessageId)) {
      setSelectedMessageId(messages[0]?.id ?? '');
    }
  }, [selectedMessageId, messages]);

  const deliveries = selectedMessage
    ? db.communicationDeliveries.filter((delivery) => delivery.communicationId === selectedMessage.id)
    : [];
  const selectedThread = selectedMessage
    ? threads.find((thread) => thread.id === selectedMessage.threadId || thread.sourceCommunicationId === selectedMessage.id)
    : undefined;
  const threadMessages = selectedThread ? listThreadMessages(selectedThread.id) : [];
  const threadDeliveries = selectedThread ? listThreadDeliveries(selectedThread.id) : [];

  const allEventDeliveries = db.communicationDeliveries.filter((delivery) => {
    const messageMatch = delivery.communicationId && messages.some((message) => message.id === delivery.communicationId);
    const threadMatch = threads.some((thread) => thread.id === delivery.threadId);
    return messageMatch || threadMatch;
  });
  const sentMessages = messages.filter((message) => message.status === 'sent');
  const counts = deliveryCounts(allEventDeliveries);
  const latestSentAt = sentMessages
    .map((message) => message.sentAt)
    .filter(Boolean)
    .sort((a, b) => String(b).localeCompare(String(a)))[0];

  return (
    <EventWorkspaceSection>
      <ModuleHero
        title="Communication history"
        description={`Review messages, delivery status, and replies tied to ${tournament.name}. New sends start from the main Comms page.`}
        icon={Megaphone}
        accent="arc"
        actions={
          canComposeForEvent ? (
            <Link to={composeLink} className="btn btn--primary">
              Compose in Comms <ExternalLink size={14} />
            </Link>
          ) : (
            <button type="button" className="btn btn--primary" disabled>
              Compose available after scheduling
            </button>
          )
        }
      />

      <div className="grid grid--4" style={{ marginBottom: 18 }}>
        <div className="stat">
          <span className="stat__label">Sent messages</span>
          <span className="stat__value">{sentMessages.length}</span>
          <span className="stat__hint">{messages.length} total event {plural(messages.length, 'record')}</span>
        </div>
        <div className="stat">
          <span className="stat__label">Latest sent</span>
          <span className="stat__value" style={{ fontSize: 18 }}>{latestSentAt ? new Date(latestSentAt).toLocaleDateString() : '-'}</span>
          <span className="stat__hint">{latestSentAt ? new Date(latestSentAt).toLocaleTimeString() : 'No sent messages yet'}</span>
        </div>
        <div className="stat">
          <span className="stat__label">Delivered</span>
          <span className="stat__value">{counts.sent + counts.delivered}</span>
          <span className="stat__hint">Email and in-app deliveries</span>
        </div>
        <div className="stat">
          <span className="stat__label">Needs attention</span>
          <span className="stat__value">{counts.failed + counts.skipped}</span>
          <span className="stat__hint">{counts.failed} failed · {counts.skipped} skipped</span>
        </div>
      </div>

      {messages.length === 0 ? (
        <div className="empty">
          <Send size={18} />
          <div style={{ fontWeight: 700, color: 'var(--text)', marginTop: 8 }}>
            No communications sent for this {tournament.format === 'season' ? 'season' : 'tournament'} yet.
          </div>
          <p className="muted" style={{ marginTop: 6 }}>
            Event-specific messages sent through Comms will appear here with delivery history and replies.
          </p>
          {canComposeForEvent ? (
            <Link to={composeLink} className="btn btn--primary btn--sm" style={{ marginTop: 12 }}>
              Compose in Comms <ExternalLink size={14} />
            </Link>
          ) : (
            <div className="note" style={{ marginTop: 12 }}>
              Event messages can be composed after this {tournament.format === 'season' ? 'season' : 'tournament'} is scheduled.
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid--2">
          <section className="card">
            <div className="card__head">
              <div>
                <h3 className="card__title">Event timeline</h3>
                <p className="card__sub">
                  {messages.length} communication {plural(messages.length, 'record')} for {tournament.name}
                </p>
              </div>
            </div>
            <div className="stack">
              {messages.map((message) => {
                const messageDeliveries = db.communicationDeliveries.filter((delivery) => delivery.communicationId === message.id);
                const author = db.profiles.find((profile) => profile.id === message.authorProfileId);
                return (
                  <button
                    key={message.id}
                    className={`thread-row ${selectedMessage?.id === message.id ? 'is-active' : ''}`}
                    onClick={() => setSelectedMessageId(message.id)}
                    type="button"
                  >
                    <span>
                      <strong>{message.subject}</strong>
                      <span className="muted">
                        {formatStatus(message.status)} · {messageDeliveries.length} {plural(messageDeliveries.length, 'delivery', 'deliveries')}
                        {author ? ` · ${author.displayName}` : ''}
                      </span>
                    </span>
                    <span className="muted">{message.sentAt ? new Date(message.sentAt).toLocaleDateString() : '-'}</span>
                  </button>
                );
              })}
            </div>
          </section>

          <section className="card">
            {selectedMessage && (
              <>
                <div className="card__head">
                  <div>
                    <h3 className="card__title">{selectedMessage.subject}</h3>
                    <p className="card__sub">
                      {formatStatus(selectedMessage.status)} · {formatDateTime(selectedMessage.sentAt)}
                    </p>
                  </div>
                  <MessageChannels message={selectedMessage} />
                </div>

                <div className="stack">
                  <div className="message-bubble">
                    <div className="row row--between" style={{ gap: 10 }}>
                      <strong>{db.profiles.find((profile) => profile.id === selectedMessage.authorProfileId)?.displayName ?? 'League HQ'}</strong>
                      <span className="muted" style={{ fontSize: 11 }}>{formatDateTime(selectedMessage.sentAt)}</span>
                    </div>
                    <div style={{ fontWeight: 700, marginTop: 6 }}>{selectedMessage.subject}</div>
                    <p style={{ margin: '6px 0 0', whiteSpace: 'pre-wrap' }}>{selectedMessage.body}</p>
                  </div>

                  <div className="note">
                    Audience: {audienceSummary(selectedMessage, db)}
                  </div>

                  <div className="note">
                    Delivery audit: {deliveries.filter((delivery) => delivery.status === 'sent' || delivery.status === 'delivered').length} sent/delivered,{' '}
                    {deliveries.filter((delivery) => delivery.status === 'queued').length} queued,{' '}
                    {deliveries.filter((delivery) => delivery.status === 'skipped').length} skipped,{' '}
                    {deliveries.filter((delivery) => delivery.status === 'failed').length} failed.
                  </div>

                  {threadMessages.length > 0 && (
                    <div className="card card--glass" style={{ boxShadow: 'none' }}>
                      <div className="card__head">
                        <div>
                          <h4 className="card__title">Thread activity</h4>
                          <p className="card__sub">
                            {threadMessages.length} {plural(threadMessages.length, 'message')} · {threadDeliveries.length} delivery audit {plural(threadDeliveries.length, 'row')}
                          </p>
                        </div>
                      </div>
                      <div className="stack">
                        {threadMessages.map((message) => (
                          <div key={message.id} className="message-bubble">
                            <div className="row row--between" style={{ gap: 10 }}>
                              <strong>{message.authorName ?? message.authorEmail ?? db.profiles.find((profile) => profile.id === message.authorProfileId)?.displayName ?? 'Recipient'}</strong>
                              <span className="muted" style={{ fontSize: 11 }}>{formatDateTime(message.createdAt)}</span>
                            </div>
                            <p style={{ margin: '6px 0 0', whiteSpace: 'pre-wrap' }}>{message.body}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </section>
        </div>
      )}
    </EventWorkspaceSection>
  );
}
